import { useState, useEffect } from "react";
import axios from "axios";
import DashboardLayout from "../../layouts/DashboardLayout";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { checkHackathonRegistration } from "../../api/hackathonRegistration";
import { fetchIdeas } from "../../api/idea";

type Hackathon = {
  _id: string;
  title: string;
  status: "Upcoming" | "Active" | "Completed";
  category: string;
  description: string;
  startDate: string;
  endDate: string;
  phases?: {
    registration: { enabled: boolean; startDate: string; endDate: string };
    submission: {
      enabled: boolean;
      startDate: string;
      endDate: string;
      initialSubmissionStart: string;
      initialSubmissionEnd: string;
      finalSubmissionStart: string;
      finalSubmissionEnd: string;
      mentorReviewDays: number;
      ideaSelection: { panelId: string | null; decisionDeadline: string };
    };
    evaluation: {
      enabled: boolean;
      startDate: string;
      endDate: string;
      rounds: Array<{
        order: number;
        name: string;
        type: string;
        startDate: string;
        endDate: string;
        panelId: string | null;
        _id: string;
      }>;
    };
  };
  themes?: Array<{ title: string; description: string }>;
  rules?: { fileName: string; fileUrl: string; fileType: string; fileSize: number };
  prizes?: Array<{ title: string; amount: number; currency: string }>;
  organizerName?: string;
  mode?: string;
  [key: string]: any;
};

const computeStatus = (startDate: string, endDate: string): "Upcoming" | "Active" | "Completed" => {
  const now = new Date();
  const start = new Date(startDate);
  const end = new Date(endDate);
  if (now < start) return "Upcoming";
  if (now >= start && now <= end) return "Active";
  return "Completed";
};

const fmt = (date?: string) =>
  date ? new Date(date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "N/A";

const statusColors = {
  Upcoming: { bg: "bg-yellow-100", text: "text-yellow-700", border: "border-yellow-300" },
  Active: { bg: "bg-blue-100", text: "text-blue-700", border: "border-blue-300" },
  Completed: { bg: "bg-green-100", text: "text-green-700", border: "border-green-300" },
};

export default function HackathonDetailPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user, token } = useAuth();
  const [hackathon, setHackathon] = useState<Hackathon | null>(null);
  const [loading, setLoading] = useState(true);
  const [isRegistered, setIsRegistered] = useState(false);
  const [submittedProblemTitle, setSubmittedProblemTitle] = useState<string | null>(null);

  useEffect(() => {
    if (!id) { setLoading(false); return; }
    const fetchHackathon = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${process.env.REACT_APP_API_URL}/hackathon-events/${id}`);
        const raw = res.data;
        const data = raw?._id ? raw : raw?.data || raw?.hackathon || raw;
        if (data?._id) {
          setHackathon({ ...data, status: computeStatus(data.startDate, data.endDate) });
        }
      } catch (err) {
        console.error("Failed to fetch hackathon:", err);
      }
      setLoading(false);
    };
    fetchHackathon();
  }, [id]);

  useEffect(() => {
    if (!hackathon || !user || !token || !id) return;
    checkHackathonRegistration(token, id)
      .then((r) => setIsRegistered(r.isRegistered))
      .catch(() => setIsRegistered(false));
  }, [hackathon, user, token, id]);

  useEffect(() => {
    if (!hackathon || !token || !id || hackathon.status !== "Active") return;
    fetchIdeas(token, { hackathonId: id })
      .then((data) => {
        const ideas: any[] = data.items ?? [];
        if (ideas.length > 0) {
          // Match submitted idea's problem text against theme titles
          const submitted = ideas[0];
          const matched = hackathon.themes?.find(
            (t) => t.title === submitted.problem || t.description === submitted.problem || t.title === submitted.title
          );
          setSubmittedProblemTitle(matched?.title ?? submitted.problem ?? null);
        }
      })
      .catch(() => {});
  }, [hackathon, token, id]);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-screen">
          <p className="text-gray-500 text-lg">Loading...</p>
        </div>
      </DashboardLayout>
    );
  }

  if (!hackathon) {
    return (
      <DashboardLayout>
        <div className="flex flex-col items-center justify-center min-h-screen gap-4">
          <p className="text-gray-500 text-lg">Hackathon not found</p>
          <button onClick={() => navigate("/user-dashboard/hackathons")} className="text-blue-600 hover:underline">
            Back to Hackathons
          </button>
        </div>
      </DashboardLayout>
    );
  }

  const sc = statusColors[hackathon.status];
  const reg = hackathon.phases?.registration;
  const sub = hackathon.phases?.submission;
  const eva = hackathon.phases?.evaluation;

  return (
    <DashboardLayout>
      <div className="min-h-screen bg-gradient-to-br from-[#ebf0ff] via-[#f0f6ff] to-[#f2fbfa]">
        {/* Hero */}
        <div className="bg-[#1a1f36] text-white px-8 py-10">
          <button
            onClick={() => navigate("/user-dashboard/hackathons")}
            className="flex items-center gap-2 text-white/80 hover:text-white text-sm mb-6"
          >
            <svg width="16" height="16" fill="none" viewBox="0 0 16 16">
              <path d="M10 12L6 8l4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            Back to Hackathons
          </button>

          <span className={`inline-block text-xs font-medium px-3 py-1 rounded-full border ${sc.bg} ${sc.text} ${sc.border} mb-3`}>
            {hackathon.status}
          </span>
          <h1 className="text-4xl font-bold mb-2">{hackathon.title}</h1>

          <div className="flex flex-wrap gap-4 mt-6">
            <div className="bg-white/10 rounded-xl px-4 py-3 min-w-[180px]">
              <p className="text-white/60 text-xs mb-1">Duration</p>
              <p className="text-white font-semibold text-sm">{fmt(hackathon.startDate)} – {fmt(hackathon.endDate)}</p>
            </div>
            {reg?.endDate && (
              <div className="bg-white/10 rounded-xl px-4 py-3 min-w-[180px]">
                <p className="text-white/60 text-xs mb-1">Registration Closes</p>
                <p className="text-white font-semibold text-sm">{fmt(reg.endDate)}</p>
              </div>
            )}
            {hackathon.themes && hackathon.themes.length > 0 && (
              <div className="bg-white/10 rounded-xl px-4 py-3 min-w-[180px]">
                <p className="text-white/60 text-xs mb-1">Idea Statements</p>
                <p className="text-white font-semibold text-sm">{hackathon.themes.length} Challenges</p>
              </div>
            )}
            {hackathon.mode && (
              <div className="bg-white/10 rounded-xl px-4 py-3 min-w-[180px]">
                <p className="text-white/60 text-xs mb-1">Mode</p>
                <p className="text-white font-semibold text-sm capitalize">{hackathon.mode}</p>
              </div>
            )}
          </div>
        </div>

        {/* Body */}
        <div className="max-w-5xl mx-auto px-6 py-10 space-y-10">
          {/* About */}
          <section className="bg-white rounded-2xl border border-gray-200 p-8">
            <h2 className="text-xl font-semibold text-[#0f172b] mb-3">About This Hackathon</h2>
            <p className="text-[#45556c] leading-relaxed">{hackathon.description}</p>
            {hackathon.organizerName && (
              <p className="mt-3 text-sm text-gray-500">Organized by: <span className="font-medium text-gray-700">{hackathon.organizerName}</span></p>
            )}
          </section>

          {/* Problem Statements */}
          {hackathon.themes && hackathon.themes.length > 0 && (
            <section>
              <h2 className="text-xl font-semibold text-[#0f172b] mb-1">Idea Statements</h2>
              <p className="text-sm text-[#45556c] mb-5">Choose a challenge that inspires you and submit your innovative solution</p>
              <div className="space-y-4">
                {hackathon.themes.map((theme, i) => {
                  const isThisSubmitted = submittedProblemTitle === theme.title;
                  const anySubmitted = submittedProblemTitle !== null;
                  return (
                  <div key={i} className="bg-white rounded-2xl border border-gray-200 p-6 flex gap-4">
                    <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center text-[#0058ab] font-bold text-lg">
                      {i + 1}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-3">
                          <h3 className="font-semibold text-[#0f172b] text-base">{theme.title}</h3>
                          <span className="text-xs px-2 py-0.5 rounded-full bg-teal-100 text-teal-700 border border-teal-200">Open</span>
                        </div>
                        {isRegistered && hackathon.status === "Active" && (
                          isThisSubmitted ? (
                            <span className="flex-shrink-0 text-sm font-medium px-4 py-1.5 rounded-lg bg-green-100 text-green-700 border border-green-200">
                              Idea Submitted ✓
                            </span>
                          ) : (
                            <button
                              disabled={anySubmitted}
                              onClick={() =>
                                navigate(
                                  `/user-dashboard/submit-idea?hackathonId=${id}&hackathonTitle=${encodeURIComponent(hackathon.title)}&problemTitle=${encodeURIComponent(theme.title)}&problemDescription=${encodeURIComponent(theme.description || theme.title)}`
                                )
                              }
                              className="flex-shrink-0 text-sm font-medium px-4 py-1.5 rounded-lg bg-[#0058ab] text-white hover:bg-[#004a92] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                              Submit Idea
                            </button>
                          )
                        )}
                      </div>
                      <p className="text-sm text-[#45556c] leading-relaxed">{theme.description}</p>
                    </div>
                  </div>
                  );
                })}
              </div>
            </section>
          )}

          {/* Documents & Resources */}
          {hackathon.rules?.fileName && (
            <section>
              <h2 className="text-xl font-semibold text-[#0f172b] mb-1">Documents & Resources</h2>
              <p className="text-sm text-[#45556c] mb-5">Download important documents and resources for this hackathon</p>
              <div className="bg-white rounded-2xl border border-gray-200 p-5 flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <svg width="20" height="20" fill="none" viewBox="0 0 20 20">
                    <path d="M4 2h8l4 4v12a2 2 0 01-2 2H4a2 2 0 01-2-2V4a2 2 0 012-2z" stroke="#0058AB" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M12 2v4h4" stroke="#0058AB" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                <div className="flex-1">
                  <p className="font-medium text-[#0f172b] text-sm">{hackathon.rules.fileName}</p>
                  {hackathon.rules.fileSize > 0 && (
                    <p className="text-xs text-[#62748e] mt-0.5">{(hackathon.rules.fileSize / 1024).toFixed(1)} KB</p>
                  )}
                </div>
                {hackathon.rules.fileUrl && (
                  <a
                    href={hackathon.rules.fileUrl}
                    download={hackathon.rules.fileName}
                    className="flex items-center justify-center w-9 h-9 rounded-lg bg-blue-50 hover:bg-blue-100 text-[#0058ab] flex-shrink-0 transition-colors"
                    title="Download document"
                  >
                    <svg width="18" height="18" fill="none" viewBox="0 0 18 18">
                      <path d="M9 2v9M5.5 7.5L9 11l3.5-3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M3 14h12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                    </svg>
                  </a>
                )}
              </div>
            </section>
          )}

          {/* Timeline */}
          {hackathon.phases && (
            <section>
              <h2 className="text-xl font-semibold text-[#0f172b] mb-1">Hackathon Timeline</h2>
              <p className="text-sm text-[#45556c] mb-5">Important phases and milestones throughout the hackathon</p>
              <div className="bg-white rounded-2xl border border-gray-200 p-8 space-y-8">
                {reg && (
                  <div className="flex flex-col items-center gap-4 justify-center text-center md:flex-row md:items-start md:justify-start md:text-left">
                    <div className="flex flex-col items-center">
                      <div className="w-10 h-10 rounded-xl bg-[#0058ab] flex items-center justify-center flex-shrink-0">
                        <svg width="20" height="20" fill="none" viewBox="0 0 20 20">
                          <rect x="3" y="4" width="14" height="14" rx="2" stroke="white" strokeWidth="1.5" />
                          <path d="M7 2v4M13 2v4M3 9h14" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
                        </svg>
                      </div>
                      <div className="w-0.5 flex-1 bg-gray-200 mt-2" />
                    </div>
                    <div className="pb-6">
                      <p className="font-semibold text-[#0f172b]">Registration Phase</p>
                      <p className="text-sm text-[#45556c] mt-0.5">{fmt(reg.startDate)} – {fmt(reg.endDate)}</p>
                    </div>
                  </div>
                )}

                {sub && (
                  <div className="flex flex-col items-center gap-4 justify-center text-center md:flex-row md:items-start md:justify-start md:text-left">
                    <div className="flex flex-col items-center">
                      <div className="w-10 h-10 rounded-xl bg-[#1dd8f2] flex items-center justify-center flex-shrink-0">
                        <svg width="20" height="20" fill="none" viewBox="0 0 20 20">
                          <path d="M10 2v10M6 8l4 4 4-4" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                          <path d="M4 16h12" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
                        </svg>
                      </div>
                      <div className="w-0.5 flex-1 bg-gray-200 mt-2" />
                    </div>
                    <div className="pb-6 flex-1">
                      <p className="font-semibold text-[#0f172b]">Submission Phase</p>
                      <div className="mt-3 pl-4 border-l-2 border-gray-200 space-y-4">
                        {sub.initialSubmissionStart && (
                          <div className="relative">
                            <span className="absolute -left-[21px] top-1 w-3 h-3 rounded-full bg-[#1dd8f2] border-2 border-white" />
                            <p className="text-sm font-medium text-[#0f172b]">Initial Idea Submission</p>
                            <p className="text-xs text-[#45556c]">{fmt(sub.initialSubmissionStart)} – {fmt(sub.initialSubmissionEnd)}</p>
                          </div>
                        )}
                        {sub.mentorReviewDays > 0 && (
                          <div className="relative">
                            <span className="absolute -left-[21px] top-1 w-3 h-3 rounded-full bg-purple-400 border-2 border-white" />
                            <p className="text-sm font-medium text-[#0f172b]">Mentor Review Period</p>
                            <p className="text-xs text-[#45556c]">{sub.mentorReviewDays} days review period</p>
                          </div>
                        )}
                        {sub.finalSubmissionStart && (
                          <div className="relative">
                            <span className="absolute -left-[21px] top-1 w-3 h-3 rounded-full bg-orange-400 border-2 border-white" />
                            <p className="text-sm font-medium text-[#0f172b]">Final Submission Window</p>
                            <p className="text-xs text-[#45556c]">{fmt(sub.finalSubmissionStart)} – {fmt(sub.finalSubmissionEnd)}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {eva && (
                  <div className="flex flex-col items-center gap-4 justify-center text-center md:flex-row md:items-start md:justify-start md:text-left">
                    <div className="flex flex-col items-center">
                      <div className="w-10 h-10 rounded-xl bg-[#008282] flex items-center justify-center flex-shrink-0">
                        <svg width="20" height="20" fill="none" viewBox="0 0 20 20">
                          <circle cx="10" cy="10" r="8" stroke="white" strokeWidth="1.5" />
                          <path d="M7 10l2 2 4-4" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </div>
                    </div>
                    <div>
                      <p className="font-semibold text-[#0f172b]">Evaluation Phase</p>
                      <p className="text-sm text-[#45556c] mt-0.5">{fmt(eva.startDate)} – {fmt(eva.endDate)}</p>
                      {eva.rounds && eva.rounds.length > 0 && (
                        <div className="mt-3 pl-4 border-l-2 border-gray-200 space-y-4">
                          {eva.rounds.map((round, i) => (
                            <div key={round._id || i} className="relative">
                              <span className="absolute -left-[21px] top-1 w-3 h-3 rounded-full bg-[#008282] border-2 border-white" />
                              <p className="text-sm font-medium text-[#0f172b]">{round.name}</p>
                              <p className="text-xs text-[#45556c]">{fmt(round.startDate)} – {fmt(round.endDate)}</p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </section>
          )}

          {/* Prizes */}
          {hackathon.prizes && hackathon.prizes.length > 0 && (
            <section>
              <h2 className="text-xl font-semibold text-[#0f172b] mb-5">Prizes</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                {hackathon.prizes.map((prize, i) => (
                  <div key={i} className="bg-white rounded-2xl border border-gray-200 p-5 text-center">
                    <p className="text-sm text-[#62748e] mb-1">{prize.title}</p>
                    <p className="text-2xl font-bold text-[#0058ab]">
                      {prize.currency} {prize.amount.toLocaleString()}
                    </p>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Spacer for floating action button */}
          <div className="h-24" />
        </div>
      </div>
      {hackathon.status !== "Completed" && (
        <div className="fixed right-10 top-24 z-50 max-w-[200px]">
          <div className="rounded-3xl bg-transparent shadow-none p-0">
            {isRegistered ? (
              <div className="bg-green-500 text-white font-medium text-sm px-4 py-2 rounded-2xl text-center">
                Already Registered ✓
              </div>
            ) : (
              <button
                disabled={hackathon.status === "Upcoming"}
                onClick={() => navigate(`/user-dashboard/hackathon-registration/${id}`)}
                className="w-full bg-[#0058ab] hover:bg-[#004a92] text-white font-medium text-base px-6 py-3 rounded-2xl transition-colors disabled:opacity-40 disabled:cursor-not-allowed focus:outline-none focus:ring-0"
                title={
                  hackathon.status === "Upcoming" ? "Registration opens when the hackathon becomes active" : undefined
                }
              >
                Register for this Hackathon
              </button>
            )}
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
